import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle2, Clock, AlertCircle, ArrowRight, Sparkles } from 'lucide-react';
import api from '../services/api';
import { ActionItem } from '../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { cn } from '../lib/utils';
import { ActionItemSummarySkeleton } from './SkeletonLoaders';
import EmptyState from './EmptyState';

const ActionItemSummary: React.FC = () => {
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchActionItems = async () => {
      // Check if token exists before making request
      const token = localStorage.getItem('authToken');
      if (!token) {
        setLoading(false);
        setError('Please log in to view action items');
        return;
      }

      try {
        setLoading(true);
        // Fetch all action items and filter on the frontend
        const response = await api.get<{ data: { data: ActionItem[] } }>('/action-items');
        // Filter to only show open and in-progress items
        // Handle paginated response structure: response.data.data is the PaginatedResponse object, response.data.data.data is the array
        const items = Array.isArray(response.data.data) ? response.data.data : (response.data.data?.data || []);
        const activeItems = items.filter(
          (item) => item.status === 'open' || item.status === 'in-progress'
        );
        setActionItems(activeItems);
        setError(null);
      } catch (err: any) {
        console.error('Failed to fetch action items:', err);
        setError(err.response?.data?.error?.message || 'Failed to load action items');
      } finally {
        setLoading(false);
      }
    };

    fetchActionItems();
  }, []);

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      open: 'status-open',
      'in-progress': 'status-in-progress',
      completed: 'status-completed',
      cancelled: 'status-cancelled',
    };
    return statusMap[status] || '';
  };

  const isOverdue = (dueDate: string): boolean => {
    return new Date(dueDate) < new Date();
  };

  const formatDueDate = (dueDate: string): string => {
    const date = new Date(dueDate);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Due today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Due tomorrow';
    } else if (date < today) {
      return `Overdue by ${Math.floor((today.getTime() - date.getTime()) / (1000 * 60 * 60 * 24))} days`;
    } else {
      return `Due ${date.toLocaleDateString()}`;
    }
  };

  const handleActionItemClick = (actionItemId: string) => {
    navigate(`/action-items/${actionItemId}`);
  };

  const getStats = () => {
    const open = actionItems.filter((item) => item.status === 'open').length;
    const inProgress = actionItems.filter((item) => item.status === 'in-progress').length;
    const overdue = actionItems.filter((item) => isOverdue(item.dueDate)).length;
    return { open, inProgress, overdue };
  };

  if (loading) {
    return <ActionItemSummarySkeleton />;
  }

  if (error) {
    return (
      <EmptyState
        emoji="⚠️"
        title="Failed to load action items"
        description={error}
        action={{
          label: 'Retry',
          onClick: () => window.location.reload(),
        }}
      />
    );
  }

  const stats = getStats();

  return (
    <Card className="shadow-sm border overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Sparkles className="h-5 w-5 text-primary" />
            Action Items
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/action-items')}
            className="h-8 text-sm gap-1"
          >
            View All <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Stats Grid - Simplified */}
        <div className="grid grid-cols-3 gap-2">
          <div className="rounded-lg bg-blue-50 p-3 text-center">
            <div className="text-2xl font-bold text-blue-700">{stats.open}</div>
            <div className="text-xs text-blue-600">Open</div>
          </div>
          <div className="rounded-lg bg-amber-50 p-3 text-center">
            <div className="text-2xl font-bold text-amber-700">{stats.inProgress}</div>
            <div className="text-xs text-amber-600">Active</div>
          </div>
          <div className="rounded-lg bg-red-50 p-3 text-center">
            <div className="text-2xl font-bold text-red-700">{stats.overdue}</div>
            <div className="text-xs text-red-600">Overdue</div>
          </div>
        </div>

        {/* Action Items List */}
        {actionItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="rounded-full bg-green-100 p-4 mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">All caught up!</h3>
            <p className="text-sm text-muted-foreground">No pending action items. Great job!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {actionItems.slice(0, 5).map((item, index) => (
              <div
                key={item.id}
                onClick={() => handleActionItemClick(item.id)}
                role="button"
                tabIndex={0}
                style={{ animationDelay: `${index * 50}ms` }}
                className={cn(
                "group relative rounded-none border-2 border-[#161E54] bg-white p-4 transition-all hover:shadow-neo hover:-translate-y-1 cursor-pointer animate-fade-in",
                isOverdue(item.dueDate) && "border-red-200 bg-red-50/30"
              )}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleActionItemClick(item.id);
                  }
                }}
              >
                <div className={cn(
                  "absolute left-0 top-0 bottom-0 w-1 transition-all group-hover:w-1.5",
                  item.status === 'open' && "bg-blue-500",
                  item.status === 'in-progress' && "bg-amber-500",
                  isOverdue(item.dueDate) && "bg-red-500"
                )} />

                <div className="flex items-start justify-between gap-3 pl-3">
                  <div className="flex-1 space-y-2">
                    <p className="text-sm font-medium text-gray-900 leading-relaxed">
                      {item.description}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge
                        variant={item.status === 'open' ? 'default' : 'secondary'}
                        className={cn(
                          "text-xs",
                          item.status === 'open' && "bg-blue-100 text-blue-700 hover:bg-blue-100",
                          item.status === 'in-progress' && "bg-amber-100 text-amber-700 hover:bg-amber-100"
                        )}
                      >
                        {item.status === 'open' ? 'Open' : 'In Progress'}
                      </Badge>
                      <span className={cn(
                        "text-xs font-medium flex items-center gap-1",
                        isOverdue(item.dueDate) ? "text-red-600" : "text-muted-foreground"
                      )}>
                        {isOverdue(item.dueDate) ? (
                          <AlertCircle className="h-3 w-3" />
                        ) : (
                          <Clock className="h-3 w-3" />
                        )}
                        {formatDueDate(item.dueDate)}
                      </span>
                    </div>
                  </div>
                  <ArrowRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-1 transition-transform flex-shrink-0 mt-1" />
                </div>
              </div>
            ))}

            {actionItems.length > 5 && (
              <div className="text-center pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/action-items')}
                  className="text-muted-foreground hover:text-foreground"
                >
                  +{actionItems.length - 5} more items
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ActionItemSummary;
